import * as React from "react"
import { Inbox } from "lucide-react"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"

/*
  EmptyState — never a bare "No data". Says what is missing, why, and what to do next.

    <EmptyState icon={SearchX} title="No projects match these filters"
                text="Try removing a filter or search by client or engineer."
                action={<Button variant="outline" onClick={clear}>Clear filters</Button>} />
    <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load stock" text={error}
                action={<Button size="sm" onClick={retry}>Retry</Button>} />

  Props: icon (lucide component), title, text (node), action (node), tone (icon tile tone, navy default),
  layout 'stacked' (centered, default) | 'inline' (icon left, for cards and table bodies),
  titleAs (heading element for the title, default 'h3' — pass 'h2' / 'h4' / 'p' to fit the page's
  heading order), className.
*/
const EmptyState = React.forwardRef(({
  icon: Icon = Inbox,
  title,
  text,
  action,
  tone = "navy",
  layout = "stacked",
  titleAs: TitleTag = "h3",
  className,
  children,
  ...props
}, ref) => {
  const inline = layout === "inline"
  return (
    <div
      ref={ref}
      data-testid={props["data-testid"] || "empty-state"}
      className={cn(
        inline
          ? "flex items-start gap-3.5 px-[18px] py-4 text-left"
          : "flex flex-col items-center gap-2 px-5 py-8 text-center",
        className
      )}
      {...props}
    >
      <span
        className={cn("ui-icon-tile", toneClass(tone), inline ? "h-10 w-10 rounded-xl" : "mb-1 h-[52px] w-[52px] rounded-2xl")}
        aria-hidden="true"
      >
        <Icon className={inline ? "h-[19px] w-[19px]" : "h-[22px] w-[22px]"} />
      </span>
      <div className={cn("flex min-w-0 flex-col gap-1", inline ? "flex-1" : "items-center")}>
        {title ? <TitleTag className="font-heading text-[15px] font-bold text-ink">{title}</TitleTag> : null}
        {text ? <div className={cn("text-[13px] text-ink-3", !inline && "max-w-[360px]")}>{text}</div> : null}
        {children}
        {action ? <div className={cn("flex flex-wrap gap-2", inline ? "mt-2" : "mt-2 justify-center")}>{action}</div> : null}
      </div>
    </div>
  )
})
EmptyState.displayName = "EmptyState"

export { EmptyState }
